import { memo } from 'react';
import { Spider } from '@mentimeter/visualizations';
import { useRagnar } from '@mentimeter/ragnar-react';
import { SvgElement } from '../../../shared/components/visualizations-legacy/SvgElement';
import { VisualizationUpdater } from '../../../shared/components/visualizations-legacy/VisualizationUpdater';
import { useUniqueSelector } from '../../../shared/hooks/useUniqueSelector';
import type {
  ScalesRange,
  ScalesResultForVisualization,
  ScalesVisualizationDataHandlerProps,
} from '../types';

interface SpiderProps {
  selector: string;
  range: ScalesRange;
  results: ScalesResultForVisualization[];
  animationMultiplier: 1 | 0;
  lineColor: string;
  fillColors: string[];
  textColor: string;
}

const SpiderComponent = memo<SpiderProps>(
  ({
    selector,
    results,
    range,
    animationMultiplier,
    fillColors,
    textColor,
    lineColor,
  }: SpiderProps) => {
    const spider = new Spider({
      selector: `#${selector}`,
      results,
      range,
      animationMultiplier,
      fillColors,
      textColor,
      lineColor,
    });

    return (
      <SvgElement aria-hidden>
        <VisualizationUpdater updateFn={spider.update} />
        <g id={selector} />
      </SvgElement>
    );
  },
);

export const SpiderVisualization = ({
  choices,
  results,
  range,
  shouldAnimate,
}: Pick<
  ScalesVisualizationDataHandlerProps,
  'results' | 'range' | 'shouldAnimate' | 'choices'
>) => {
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
    },
  } = useRagnar();
  const selector = useUniqueSelector();

  const animationMultiplier = shouldAnimate ? 1 : 0;

  const resultsWithFallback = choices.map((choice) => {
    const result = results.find(
      (result) => result.id === choice.interactiveContentChoiceId,
    );

    return (
      result ?? {
        id: choice.interactiveContentChoiceId,
        label: choice.title,
        score: {},
        skips: 0,
        identifiedResults: [],
      }
    );
  });

  return (
    <SpiderComponent
      selector={selector}
      results={resultsWithFallback}
      fillColors={fillColors}
      textColor={textColor}
      lineColor={textColor}
      animationMultiplier={animationMultiplier}
      range={range}
    />
  );
};
